import { unwrapResponse, wrapRequest } from "./crypto";
import { apiHeaders, apiHeadersJson } from "./headers";
import { clearToken, setToken } from "./token";
const BASE = "/api/orchestrator/auth";

export interface Session {
    authenticated: boolean;
    mode: string;
    /** False when the server has no credentials at all, so a login cannot succeed. */
    configured: boolean;
    session_secret_set: boolean;
    /** True only when a username/password sign-in can actually work. */
    login_available: boolean;
    subject?: string;
    /** Stable account id from console_users. Null for an .env or service caller. */
    user_id?: string | null;
    email?: string | null;
    tenants?: string[];
    /**
     * "admin" or "member". Decides whether the Users screen is offered.
     *
     * Presentation only — every /users route checks this again server-side. A
     * hidden nav link stops nobody from typing the URL.
     */
    role?: string;
    /**
     * Everything this account may do, e.g. "documents:export". Resolved from
     * the role on the server, per request — a role edited in the console takes
     * effect on the holder's next request, not at their next sign-in.
     */
    permissions?: string[];
    /** Route path -> the permission that gates it. The server owns this map. */
    pages?: Record<string, string>;
    via?: string;
}

/** What POST /auth/login answers with. */
export interface LoginResult {
    status: string;
    authenticated: boolean;
    subject?: string;
    user_id?: string | null;
    email?: string | null;
    tenants?: string[];
    /** Always "Bearer". */
    token_type?: string;
    /** Send as `Authorization: Bearer <access_token>` on any other API. */
    access_token?: string;
    /** Lifetime in seconds. */
    expires_in?: number;
}

/**
 * The session cookie is HttpOnly, so this is the only way the app can know
 * whether it is signed in — it cannot read the cookie itself. That is the
 * point: script that cannot read the cookie cannot exfiltrate it either.
 */
export async function fetchSession(): Promise<Session> {
    const res = await fetch(`${BASE}/session`, { headers: apiHeaders() });
    if (!res.ok) throw new Error(`Session check failed: ${res.status}`);
    // /auth/session is served plain even when encryption is on -- it is read
    // before a client is in any position to decrypt -- but unwrapResponse
    // decides per response, so routing it through costs nothing and would
    // keep working if that ever changed.
    return unwrapResponse<Session>(await res.json());
}

export async function login(username: string, password: string): Promise<LoginResult> {
    const res = await fetch(`${BASE}/login`, {
        method: "POST",
        headers: apiHeadersJson(),
        body: await wrapRequest({ username, password }),
    });

    if (!res.ok) {
        let message = `Sign-in failed (${res.status}).`;
        try {
            const body = await res.json();
            if (body?.message) message = body.message;
        } catch {
            /* non-JSON error body */
        }
        throw new Error(message);
    }

    const body = await unwrapResponse<LoginResult>(await res.json());
    // Stored before returning, so the caller's next request -- the session
    // re-check that decides whether to leave the login screen -- already
    // carries it. A server that stops returning one is not an error here: the
    // cookie was set by the same response and the console still works.
    if (body?.access_token) setToken(body.access_token);
    return body;
}

export async function logout(): Promise<void> {
    // Sent first, while the header can still identify who is signing out, and
    // cleared afterwards whatever the server said -- a failed logout call must
    // not leave a usable token sitting in this tab.
    try {
        await fetch(`${BASE}/logout`, { method: "POST", headers: apiHeaders() });
    } finally {
        clearToken();
    }
}
